import { Dispatch, SetStateAction } from 'react';

import { EmailBasicDto, EmailSendModeDto, ResourceIdDto } from '@/api/generated';
import {
  NewEmailAttachmentModel,
  NewEmailUpdateAttachmentModel,
  UUID,
} from '@/components/emailWindow/components/attachments/model/NewEmailAttachment.model.ts';
import { NewEmailFormModel } from '@/components/emailWindow/model/NewEmailForm.model.ts';

export enum EditedEmailState {
  IN_PROGRESS = 'IN_PROGRESS',
  READY = 'READY',
}

export enum NEW_EMAIL_REDUCER_EVENT {
  ATTACHMENT_ADD = 'ATTACHMENT_ADD',
  ATTACHMENT_REMOVE = 'ATTACHMENT_REMOVE',
  ATTACHMENT_SET = 'ATTACHMENT_SET',
  ATTACHMENT_SET_READY = 'ATTACHMENT_SET_READY',
  ATTACHMENT_UPDATE_ID = 'ATTACHMENT_UPDATE_ID',
  CHANGE_WINDOW_MODE = 'CHANGE_WINDOW_MODE',
  CLEAR_COMPOSER = 'CLEAR_COMPOSER',
  DRAFT_READY = 'DRAFT_READY',
  OPEN_EXISTING_DRAFT = 'OPEN_EXISTING_DRAFT',
  SEND_SUCCESS = 'SEND_SUCCESS',
  WRITE_EMAIL = 'WRITE_EMAIL',
}

export enum NEW_EMAIL_VIEW_MODE {
  DOCKED = 'DOCKED',
  FLOATING_WINDOW = 'FLOATING_WINDOW',
}

export type NewEmailComposerBase = {
  attachments: NewEmailAttachmentModel[];
  draftRequestId: string;
  email: Omit<EmailBasicDto, 'attachments'> | null;
  form?: NewEmailFormModel;
  isCollapsed?: boolean;
  mode: EmailSendModeDto;
  resource?: ResourceIdDto;
  windowMode?: NEW_EMAIL_VIEW_MODE;
};

export type NewEmailComposerState =
  | (NewEmailComposerBase & {
      emailId: string;
      state: EditedEmailState.READY;
      threadId: string;
    })
  | (NewEmailComposerBase & {
      state: EditedEmailState.IN_PROGRESS;
    });

export type NewEmailContextProps = {
  autosavePaused: boolean;
  newEmailState: NewEmailComposerState | null;
  setAutosavePaused: Dispatch<SetStateAction<boolean>>;
  updateNewEmailState: NewEmailStateDispatch;
};

export type NewEmailReducerAction =
  | EmailsReducerActionBasic<NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_ADD, NewEmailAttachmentModel>
  | EmailsReducerActionBasic<NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_REMOVE, { uuid: UUID }>
  | EmailsReducerActionBasic<NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_SET_READY, { attachmentId: string }>
  | EmailsReducerActionBasic<NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_SET, NewEmailAttachmentModel[]>
  | EmailsReducerActionBasic<
      NEW_EMAIL_REDUCER_EVENT.ATTACHMENT_UPDATE_ID,
      NewEmailUpdateAttachmentModel
    >
  | EmailsReducerActionBasic<
      NEW_EMAIL_REDUCER_EVENT.CHANGE_WINDOW_MODE,
      { form?: NewEmailFormModel; isCollapsed?: true; mode: NEW_EMAIL_VIEW_MODE }
    >
  | EmailsReducerActionBasic<NEW_EMAIL_REDUCER_EVENT.CLEAR_COMPOSER, Record<never, never>>
  | EmailsReducerActionBasic<
      NEW_EMAIL_REDUCER_EVENT.DRAFT_READY,
      { draftRequestId: string; emailId: string; threadId: string }
    >
  | EmailsReducerActionBasic<
      NEW_EMAIL_REDUCER_EVENT.OPEN_EXISTING_DRAFT,
      NewEmailViewStateWritingEmail & { emailId: string; threadId: string }
    >
  | EmailsReducerActionBasic<NEW_EMAIL_REDUCER_EVENT.SEND_SUCCESS, Record<never, never>>
  | EmailsReducerActionBasic<NEW_EMAIL_REDUCER_EVENT.WRITE_EMAIL, NewEmailViewStateWritingEmail>;

export type NewEmailStateDispatch = (action: NewEmailReducerAction) => void;

export type NewEmailViewStateWritingEmail = NewEmailComposerBase;

type EmailsReducerActionBasic<TYPE extends NEW_EMAIL_REDUCER_EVENT, PAYLOAD extends object> = {
  payload: PAYLOAD;
  type: TYPE;
};
